import {gql} from '@apollo/client';
import {useCallback} from 'react';

import {ProteinSchemasSummaryQuery} from 'antibodies/hooks/__generated/ProteinSchemasSummaryQuery';
import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

type RegistryForProteinSchemasSummaryQuery = ProteinSchemasSummaryQuery['registries'][number];

export interface ProteinSchemaSummary {
  schema: {
    id: string;
  };
  format: {
    id: string;
  };
}

function getUnarchivedProteinSchemaSummaries(
  registries: Array<RegistryForProteinSchemasSummaryQuery>
): Array<ProteinSchemaSummary> {
  return registries.flatMap((registry) =>
    registry.__typename === 'RegistryNode'
      ? registry.antibodySchemas.map((schema) => ({
          schema: {id: schema.id},
          format: {id: schema.format.id},
        }))
      : []
  );
}

export const PROTEIN_SCHEMAS_SUMMARY_QUERY = gql`
  query ProteinSchemasSummaryQuery @skipAudit @engComponent(name: ANTIBODIES) {
    registries {
      ... on RegistryNode {
        id
        antibodySchemas(includeArchived: false) {
          id
          format {
            id
          }
        }
      }
    }
  }
`;

export function useUnarchivedProteinSchemasSummaryQuery(): {
  status: QueryResultType;
  unarchivedProteinSchemas: Array<ProteinSchemaSummary>;
  refetch: () => Promise<Array<ProteinSchemaSummary>>;
} {
  const result = useSkippableBenchlingQuery<ProteinSchemasSummaryQuery>(PROTEIN_SCHEMAS_SUMMARY_QUERY, {
    skip: !isBiologicsEnabled(),
  });

  const queryRefetch = result.refetch;

  const refetch = useCallback(async (): Promise<Array<ProteinSchemaSummary>> => {
    const refetchResult = await queryRefetch();
    const registries = refetchResult.data?.registries ?? [];
    return getUnarchivedProteinSchemaSummaries(registries);
  }, [queryRefetch]);

  if (result.type !== QueryResultType.SUCCESS) {
    return {status: result.type, unarchivedProteinSchemas: [], refetch};
  }

  return {
    status: QueryResultType.SUCCESS,
    unarchivedProteinSchemas: getUnarchivedProteinSchemaSummaries(result.data.registries),
    refetch,
  };
}
